import React, { useEffect, useState, useCallback } from 'react';
import './welcomeTour.css';

/*
 * WelcomeTour (T7, Option A) — a once-only 3-slide welcome modal shown to
 * brand-new accounts (see the first-run detection in App.js) and replayable
 * via the "Show tutorial" footer link.
 *
 * Overlay/shell pattern imitates src/components/sources/SourceWizard.js
 * (fixed overlay, dots progress, header ✕, footer actions) but this is a
 * pure linear carousel — no branching steps, no form state.
 *
 * Props:
 *   onClose() — called on ANY way of leaving the tour (✕, Skip, Esc, or the
 *               final slide's CTA). The caller (App.js) is responsible for
 *               persisting `setTutorialDone(sub)` inside this callback —
 *               WelcomeTour itself holds no identity/user info.
 *
 * Content note: slide 1's three feature chips are reused verbatim from the
 * landing page (App.js `feature-highlights` block) — do not invent new copy
 * here if that block's wording changes, update both together.
 */

const FEATURE_CHIPS = [
  { icon: '🌍', text: 'Learn New Languages' },
  { icon: '🎙️', text: 'Stay Updated on the News' },
  { icon: '⏰', text: 'Scheduled Daily Podcast' }
];

const FLOW_STEPS = [
  { icon: '📰', label: 'News Sources' },
  { icon: '📅', label: 'Schedules' },
  { icon: '🎧', label: 'Episodes' }
];

const SLIDE_COUNT = 3;

function WelcomeTour({ onClose }) {
  const [slideIndex, setSlideIndex] = useState(0);

  const isLast = slideIndex === SLIDE_COUNT - 1;

  const goNext = useCallback(() => {
    if (isLast) {
      onClose();
    } else {
      setSlideIndex((i) => Math.min(i + 1, SLIDE_COUNT - 1));
    }
  }, [isLast, onClose]);

  const goPrev = useCallback(() => {
    setSlideIndex((i) => Math.max(i - 1, 0));
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, goNext, goPrev]);

  const renderSlide = () => {
    if (slideIndex === 0) {
      return (
        <div className="welcome-tour__slide">
          <h3 className="welcome-tour__slide-title">What LinguaNewsCast does</h3>
          <p className="welcome-tour__slide-text">
            LinguaNewsCast turns the news you choose into your own podcast episodes, read aloud in
            a language you're learning.
          </p>
          <div className="welcome-tour__chips">
            {FEATURE_CHIPS.map((chip) => (
              <div key={chip.text} className="welcome-tour__chip">
                <span className="welcome-tour__chip-icon" aria-hidden="true">{chip.icon}</span>
                <span className="welcome-tour__chip-text">{chip.text}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slideIndex === 1) {
      return (
        <div className="welcome-tour__slide">
          <h3 className="welcome-tour__slide-title">How it flows</h3>
          <p className="welcome-tour__slide-text">Your podcast, in three steps:</p>
          <div className="welcome-tour__flow">
            {FLOW_STEPS.map((step, index) => (
              <React.Fragment key={step.label}>
                <div className="welcome-tour__flow-step">
                  <span className="welcome-tour__flow-icon" aria-hidden="true">{step.icon}</span>
                  <span className="welcome-tour__flow-label">{step.label}</span>
                </div>
                {index < FLOW_STEPS.length - 1 && (
                  <span className="welcome-tour__flow-arrow" aria-hidden="true">→</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="welcome-tour__slide">
        <h3 className="welcome-tour__slide-title">Let's set up your first podcast</h3>
        <p className="welcome-tour__slide-text">
          Head to the checklist on your Home tab — it walks you through adding a news source,
          creating a schedule, and getting your first episode.
        </p>
      </div>
    );
  };

  return (
    <div className="welcome-tour__overlay" role="dialog" aria-modal="true" aria-label="Welcome tour">
      <div className="welcome-tour">
        <button
          type="button"
          className="welcome-tour__close"
          aria-label="Skip tour"
          onClick={onClose}
        >
          ✕
        </button>

        <div className="welcome-tour__dots">
          {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
            <div
              key={i}
              className={`welcome-tour__dot${i === slideIndex ? ' welcome-tour__dot--active' : ''}`}
            />
          ))}
        </div>

        <div className="welcome-tour__body">{renderSlide()}</div>

        <div className="welcome-tour__footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Skip
          </button>
          <button type="button" className="btn btn-primary" onClick={goNext}>
            {isLast ? "Let's go" : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WelcomeTour;
